/*
 * ELCHIS theme – collection & search filtering (Shopify Storefront Filtering)
 * Filters, sorting, chips and "Mehr anzeigen" update the page through the Section Rendering
 * API. Every state has a real URL (history.pushState), so filtered views are shareable and
 * the browser back button works. Without JS the filter form submits normally.
 */
(() => {
  if (!window.Elchis || window.ElchisFacets) return;
  window.ElchisFacets = true;

  const { define, debounce, parseHTML, toast, announce, openDialog, strings } = window.Elchis;
  const desktop = window.matchMedia('(min-width: 1024px)');

  class FacetResults extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      if (this._bound) return;
      this._bound = true;

      this.applyDebounced = debounce(() => this.apply(), 350);
      this.applyPriceDebounced = debounce(() => this.apply(), 700);

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (target.matches('[data-sort]')) {
          this.apply();
        } else if (target.closest('[data-facets-form]')) {
          if (target.type === 'number') this.applyPriceDebounced();
          else this.applyDebounced();
        }
      });

      this.addEventListener('input', (event) => {
        if (event.target.type === 'number' && event.target.closest('[data-facets-form]')) this.applyPriceDebounced();
      });

      this.addEventListener('submit', (event) => {
        if (!event.target.matches('[data-facets-form]')) return;
        event.preventDefault();
        this.apply();
      });

      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-link], [data-facets-clear]');
        if (link) {
          event.preventDefault();
          this.load(link.href, { push: true });
          return;
        }
        const more = event.target.closest('[data-load-more]');
        if (more) {
          event.preventDefault();
          this.loadMore(more);
          return;
        }
        const open = event.target.closest('[data-facets-open]');
        if (open) {
          const dialog = this.querySelector('.facets-dialog');
          if (dialog && !desktop.matches) openDialog(dialog, open);
        }
      });

      this.lastSearch = window.location.search;
      this.onPopState = () => {
        if (window.location.search === this.lastSearch) return;
        this.load(window.location.href, { push: false });
      };
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    form() {
      return document.getElementById(`FacetsForm-${this.sectionId}`);
    }

    apply() {
      const form = this.form();
      if (!form) return;
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      const url = `${form.getAttribute('action')}?${params.toString()}`;
      this.load(url, { push: true });
    }

    sectionUrl(url) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', this.sectionId);
      return u.toString();
    }

    async load(url, { push }) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(this.sectionUrl(url), { signal: this.controller.signal });
        if (!response.ok) throw new Error(strings.genericError);
        const doc = parseHTML(await response.text());
        this.render(doc);
        if (push) {
          const clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          window.history.pushState({ facets: true }, '', clean.pathname + clean.search);
        }
        this.lastSearch = window.location.search;
        const count = this.querySelector('.col-toolbar__count');
        if (count) announce(count.textContent.trim());
      } catch (error) {
        if (error.name !== 'AbortError') toast({ message: strings.genericError, type: 'error' });
      } finally {
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
      }
    }

    render(doc) {
      const incoming = doc.querySelector('facet-results');
      if (!incoming) return;
      const active = document.activeElement;
      const focusKey = active && this.contains(active)
        ? (active.id ? `#${CSS.escape(active.id)}` : (active.name ? `[name="${CSS.escape(active.name)}"][value="${CSS.escape(active.value)}"]` : null))
        : null;

      // Toolbar + grid are replaced; the filter dialog keeps its element (it may be open on
      // mobile) and only its form contents, footer count and reset link are refreshed.
      ['toolbar', 'grid'].forEach((part) => {
        const current = this.querySelector(`[data-facet-part="${part}"]`);
        const next = incoming.querySelector(`[data-facet-part="${part}"]`);
        if (current && next) current.replaceWith(next);
      });

      const currentForm = this.form();
      const nextForm = incoming.querySelector(`#FacetsForm-${CSS.escape(this.sectionId)}`);
      if (currentForm && nextForm) {
        const openState = [...currentForm.querySelectorAll('details.facet')].map((d) => d.open);
        currentForm.innerHTML = nextForm.innerHTML;
        currentForm.querySelectorAll('details.facet').forEach((d, i) => {
          if (typeof openState[i] === 'boolean') d.open = openState[i] || d.open;
        });
      }
      const countBtn = this.querySelector('[data-facets-count-btn]');
      const nextCount = incoming.querySelector('[data-facets-count-btn]');
      if (countBtn && nextCount) countBtn.textContent = nextCount.textContent;
      const clear = this.querySelector('[data-facets-clear]');
      const nextClear = incoming.querySelector('[data-facets-clear]');
      if (clear && nextClear) clear.href = nextClear.getAttribute('href');

      if (focusKey) {
        try {
          const again = this.querySelector(focusKey);
          if (again) again.focus({ preventScroll: true });
        } catch (error) { /* selector not valid for this value */ }
      }
    }

    async loadMore(button) {
      const grid = this.querySelector('[data-product-grid]');
      if (!grid) return;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
      try {
        const response = await fetch(this.sectionUrl(button.href));
        if (!response.ok) throw new Error(strings.genericError);
        const doc = parseHTML(await response.text());
        const incomingGrid = doc.querySelector('[data-product-grid]');
        const items = incomingGrid ? [...incomingGrid.children] : [];
        const firstNew = items[0];
        items.forEach((item) => grid.appendChild(document.importNode(item, true)));

        const nav = this.querySelector('.pager-nav');
        const nextNav = doc.querySelector('.pager-nav');
        const shown = grid.querySelectorAll(':scope > li:not(.grid__tile)').length;
        const totalMatch = nav ? (nav.querySelector('[data-load-status]') || nav).textContent.match(/\d+(?!.*\d)/) : null;
        if (nav && nextNav && nextNav.querySelector('[data-load-more]')) {
          nav.replaceWith(document.importNode(nextNav, true));
        } else if (nav) {
          nav.remove();
        }
        const message = totalMatch && strings.showing ? strings.showing.replace('[shown]', shown).replace('[total]', totalMatch[0]) : '';
        const status = this.querySelector('[data-load-status]');
        if (status && message) status.textContent = message;
        announce(message);

        if (firstNew) {
          const link = grid.children[grid.children.length - items.length].querySelector('a.card__link, a');
          if (link) link.focus({ preventScroll: false });
        }
      } catch (error) {
        toast({ message: strings.genericError, type: 'error' });
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      }
    }
  }
  define('facet-results', FacetResults);
})();
